import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiTrash2, FiAlertTriangle } from 'react-icons/fi'
import { FileItem } from '@/types'

interface DeleteFileConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  file: FileItem | null
  isLoading?: boolean
}

export default function DeleteFileConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  file,
  isLoading = false,
}: DeleteFileConfirmationModalProps) {
  const warningColor = useColorModeValue('orange.500', 'orange.300')
  const deleteColor = useColorModeValue('red.500', 'red.300')

  if (!file) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={FiAlertTriangle} color={warningColor} boxSize={6} />
            <Text>Delete File</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="start">
            <Text>
              Are you sure you want to delete this file? This action will move the file to trash.
            </Text>

            <VStack
              spacing={2}
              align="start"
              p={4}
              bg={useColorModeValue('gray.50', 'gray.700')}
              borderRadius="md"
              w="full"
            >
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={useColorModeValue('gray.700', 'gray.300')}
              >
                File to delete:
              </Text>
              <HStack spacing={2}>
                <Icon as={FiTrash2} color={deleteColor} boxSize={4} />
                <Text fontSize="sm" fontWeight="medium">
                  {file.display_name || file.original_name}
                </Text>
              </HStack>
            </VStack>

            <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')}>
              You can restore this file from the trash later if needed.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              leftIcon={<FiTrash2 />}
              onClick={onConfirm}
              isLoading={isLoading}
              loadingText="Deleting..."
            >
              Delete
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
